import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  Upload,
  XCircle,
  FileCheck2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PortalApiError } from "@/lib/portalApi";
import {
  ACCEPTED_UPLOAD_TYPES,
  DOCUMENT_TYPES,
  MAX_UPLOAD_BYTES,
  publicOnboardingApi,
  type CaptureFields,
  type EmployeeType,
  type PublicOnboardingView,
} from "@/services/api/publicOnboarding";

export const Route = createFileRoute("/onboard/$token")({
  component: OnboardCapturePage,
});

// Statuses from which a candidate may still (re)capture — mirrors the backend
// _SUBMITTABLE_STATUSES. Anything past this is locked to the candidate.
const SUBMITTABLE = new Set(["draft", "invited", "captured"]);

const EMPLOYEE_TYPE_LABEL: Record<EmployeeType, string> = {
  monthly_salaried: "Monthly salaried",
  hourly: "Hourly",
  commission: "Commission",
};

const ACCOUNT_TYPES = ["cheque", "savings", "transmission"] as const;

type FieldKey = keyof CaptureFields;

// Explicit allow-list of the fields we prefill and submit. Using this instead of
// spreading the GET view means a future display-only field added to the view
// (org name, invited_at, …) can never silently flow back into the POST body.
const CAPTURE_KEYS: FieldKey[] = [
  "first_name",
  "last_name",
  "email",
  "mobile",
  "national_id_number",
  "date_of_birth",
  "tax_number",
  "bank_name",
  "branch_code",
  "account_number",
  "account_type",
  "emergency_contact_name",
  "emergency_contact_phone",
];

function mapLoadError(err: unknown): string {
  if (err instanceof PortalApiError) {
    // 404 is the generic non-enumeration-safe "invalid/expired/used".
    if (err.status === 404)
      return "This onboarding link is invalid or has expired. Please ask your HR contact for a new link.";
    if (err.status === 429)
      return "Too many requests just now. Please wait a minute and refresh.";
    return err.message;
  }
  // No status → a network/transport failure (common on mobile). Do NOT tell the
  // candidate their link is bad — it very likely isn't.
  return "We couldn't load this page. Please check your connection and try again.";
}

function OnboardCapturePage() {
  const { token } = Route.useParams();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [context, setContext] = useState<{
    employee_type: EmployeeType;
    region: string;
  } | null>(null);

  const [fields, setFields] = useState<CaptureFields>({});
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  // Snapshot of the server-prefilled values, to detect unsaved edits.
  const loadedSnapshot = useRef<string>("{}");

  useEffect(() => {
    let cancelled = false;
    publicOnboardingApi
      .get(token)
      .then((view: PublicOnboardingView) => {
        if (cancelled) return;
        setContext({ employee_type: view.employee_type, region: view.region });
        const prefill: CaptureFields = {};
        CAPTURE_KEYS.forEach((k) => {
          const v = view[k];
          if (v != null) prefill[k] = v;
        });
        setFields(prefill);
        loadedSnapshot.current = JSON.stringify(prefill);
        if (!SUBMITTABLE.has(view.status)) setLocked(true);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(mapLoadError(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Warn before an accidental back/refresh wipes a partly-filled form (mobile).
  const dirty = JSON.stringify(fields) !== loadedSnapshot.current;
  useEffect(() => {
    if (!dirty || submitted || locked) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, submitted, locked]);

  const setField = (key: FieldKey, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<FieldKey, string>> = {};
    if (!fields.first_name?.trim()) next.first_name = "Required";
    if (!fields.last_name?.trim()) next.last_name = "Required";
    if (!fields.national_id_number?.trim())
      next.national_id_number = "Required";
    if (!fields.email?.trim() && !fields.mobile?.trim())
      next.email = "Enter an email or a mobile number";
    if (fields.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email))
      next.email = "Enter a valid email address";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    // Trim strings and send blanks as null; only ever send the capture fields.
    const payload: CaptureFields = {};
    CAPTURE_KEYS.forEach((k) => {
      const v = fields[k];
      payload[k] =
        typeof v === "string" ? (v.trim() === "" ? null : v.trim()) : (v ?? null);
    });
    try {
      await publicOnboardingApi.submit(token, payload);
      loadedSnapshot.current = JSON.stringify(fields); // clean → no unload warning
      setSubmitted(true);
    } catch (err: unknown) {
      if (err instanceof PortalApiError && err.status === 409) {
        setLocked(true);
      } else if (err instanceof PortalApiError && err.status === 429) {
        toast.error("Too many attempts. Please wait a minute and try again.");
      } else {
        toast.error(
          err instanceof PortalApiError
            ? err.message
            : "Something went wrong submitting your details. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <Helmet>
        <title>Complete your onboarding | Larasoft</title>
        {/* Tokenised PII URL — never index or follow. */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="mx-auto w-full max-w-lg px-4 py-8 sm:px-6">
        <div className="mb-6 flex justify-center">
          <img
            src="/Larasoft Logo-landscape.svg"
            alt="Larasoft"
            className="h-10"
            style={{ width: "auto" }}
          />
        </div>

        {loading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        ) : loadError ? (
          <StatusCard
            icon={<XCircle className="mx-auto h-12 w-12 text-destructive" />}
            title="Link unavailable"
            description={loadError}
          />
        ) : submitted ? (
          <StatusCard
            icon={
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
            }
            title="Thank you!"
            description="Your details have been submitted to HR. There's nothing more to do — we'll be in touch if anything else is needed."
          />
        ) : locked ? (
          <StatusCard
            icon={
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
            }
            title="Already submitted"
            description="This onboarding has already been submitted and is with HR. If you need to change something, please contact your HR representative."
          />
        ) : (
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="text-center">
              <h1 className="text-xl font-semibold">Complete your onboarding</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {context
                  ? `${EMPLOYEE_TYPE_LABEL[context.employee_type]} position — please confirm your details below.`
                  : "Please confirm your details below."}
              </p>
            </div>

            <Section title="Your details">
              <Field
                id="first_name"
                label="First name"
                required
                value={fields.first_name}
                error={errors.first_name}
                onChange={(v) => setField("first_name", v)}
                autoComplete="given-name"
              />
              <Field
                id="last_name"
                label="Last name"
                required
                value={fields.last_name}
                error={errors.last_name}
                onChange={(v) => setField("last_name", v)}
                autoComplete="family-name"
              />
              <Field
                id="national_id_number"
                label="ID / passport number"
                required
                value={fields.national_id_number}
                error={errors.national_id_number}
                onChange={(v) => setField("national_id_number", v)}
                inputMode="numeric"
              />
              <Field
                id="date_of_birth"
                label="Date of birth"
                type="date"
                value={fields.date_of_birth}
                onChange={(v) => setField("date_of_birth", v)}
              />
              <Field
                id="email"
                label="Email"
                type="email"
                value={fields.email}
                error={errors.email}
                onChange={(v) => setField("email", v)}
                autoComplete="email"
              />
              <Field
                id="mobile"
                label="Mobile number"
                type="tel"
                value={fields.mobile}
                onChange={(v) => setField("mobile", v)}
                autoComplete="tel"
              />
            </Section>

            <Section title="Tax">
              <Field
                id="tax_number"
                label="Tax reference number"
                value={fields.tax_number}
                onChange={(v) => setField("tax_number", v)}
                inputMode="numeric"
              />
            </Section>

            <Section title="Banking (for your salary)">
              <Field
                id="bank_name"
                label="Bank name"
                value={fields.bank_name}
                onChange={(v) => setField("bank_name", v)}
              />
              <Field
                id="branch_code"
                label="Branch code"
                value={fields.branch_code}
                onChange={(v) => setField("branch_code", v)}
                inputMode="numeric"
              />
              <Field
                id="account_number"
                label="Account number"
                value={fields.account_number}
                onChange={(v) => setField("account_number", v)}
                inputMode="numeric"
              />
              <div className="space-y-1.5">
                <Label htmlFor="account_type">Account type</Label>
                <Select
                  value={fields.account_type ?? undefined}
                  onValueChange={(v) => setField("account_type", v)}
                >
                  <SelectTrigger id="account_type">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </Section>

            <Section title="Emergency contact">
              <Field
                id="emergency_contact_name"
                label="Contact name"
                value={fields.emergency_contact_name}
                onChange={(v) => setField("emergency_contact_name", v)}
              />
              <Field
                id="emergency_contact_phone"
                label="Contact number"
                type="tel"
                value={fields.emergency_contact_phone}
                onChange={(v) => setField("emergency_contact_phone", v)}
              />
            </Section>

            <DocumentsSection token={token} />

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={submitting}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit my details
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Your information is sent securely to your employer's HR team.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function StatusCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardHeader className="text-center">
        {icon}
        <CardTitle className="mt-4">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
    </Card>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  error,
  required,
  type = "text",
  inputMode,
  autoComplete,
}: {
  id: FieldKey;
  label: string;
  value?: string | null;
  onChange: (v: string) => void;
  error?: string;
  required?: boolean;
  type?: string;
  inputMode?: "numeric" | "tel" | "email" | "text";
  autoComplete?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      <Input
        id={id}
        name={id}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={error ? "border-destructive" : undefined}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function DocumentsSection({ token }: { token: string }) {
  return (
    <Section title="Documents (optional)">
      <p className="-mt-1 text-xs text-muted-foreground">
        PDF or photo, up to 10 MB each.
      </p>
      {DOCUMENT_TYPES.map((dt) => (
        <DocumentRow
          key={dt.value}
          token={token}
          value={dt.value}
          label={dt.label}
        />
      ))}
    </Section>
  );
}

function DocumentRow({
  token,
  value,
  label,
}: {
  token: string;
  value: string;
  label: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`${label} is larger than 10 MB.`);
      return;
    }
    setUploading(true);
    try {
      const doc = await publicOnboardingApi.uploadDocument(token, value, file);
      setUploadedName(doc.filename ?? file.name);
      toast.success(`${label} uploaded.`);
    } catch (err: unknown) {
      const message =
        err instanceof PortalApiError && err.status === 413
          ? `${label} is larger than 10 MB.`
          : err instanceof PortalApiError
            ? err.message
            : "Upload failed. Please try again.";
      toast.error(message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{label}</p>
        {uploadedName && (
          <p className="flex items-center gap-1 truncate text-xs text-green-600">
            <FileCheck2 className="h-3 w-3 shrink-0" />
            {uploadedName}
          </p>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_UPLOAD_TYPES}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Upload className="mr-1.5 h-4 w-4" />
            {uploadedName ? "Replace" : "Upload"}
          </>
        )}
      </Button>
    </div>
  );
}
